import { AppView } from '../types';

// Merkt sich die aktuelle Ansicht über einen Browser-Refresh hinweg
// (sessionStorage: überlebt F5, stirbt mit dem Tab — bewusst kein
// localStorage, sonst klebt die App für immer an der letzten Ansicht).
// Pure Funktionen, Storage injiziert: testbar ohne DOM.

export const NAV_STATE_KEY = 'osia_nav_state_v1';

export interface NavState {
  view: AppView;
  projectId: string | null;
  activeSection: string | null;
  /**
   * War das Projekt schreibgeschuetzt geoeffnet ("Offene Positionen")?
   *
   * Ohne diese Flagge kam ein so geoeffnetes Projekt nach F5 im SCHREIBMODUS
   * zurueck: kein gelber Balken, kein "Nicht gespeichert (Nur-Ansicht)", alle
   * Riegel offen — und der Nutzer schrieb ahnungslos in ein fremdes Projekt.
   * Pflichtfeld, damit keine Aufrufstelle sie stillschweigend weglaesst.
   *
   * Nur fuer Projekt-Ansichten von Bedeutung; bei 'plain' gibt es nichts zu
   * schuetzen.
   */
  isReadOnly: boolean;
}

interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

/**
 * Jede Ansicht der Anwendung und ihre Art.
 *
 * VOLLSTAENDIG, und der Typ erzwingt das: `Record<AppView, ...>` verlangt jeden
 * Wert aus AppView als Schluessel. Vorher standen hier zwei Positivlisten, und
 * eine neue Ansicht, die niemand nachtrug, wurde stillschweigend nicht gemerkt.
 * 'crm', 'quotes' und 'invoices' fehlten alle drei — nach einem Neuladen landete
 * man dort in der zuletzt gemerkten anderen Ansicht, ohne jede Meldung.
 *
 * 'project' = funktioniert nur mit geladenem Projekt und braucht deshalb eine
 * projectId im gemerkten Zustand. 'plain' = steht fuer sich.
 */
const VIEW_KIND: Record<AppView, 'project' | 'plain'> = {
  dashboard: 'plain',
  projects: 'plain',
  admin: 'plain',
  'admin-projects': 'plain',
  'admin-settings': 'plain',
  users: 'plain',
  'project-management': 'plain',
  // Verwaltungsbereiche aus dem Menue-Umbau 19.10.2026 (adminBereiche.ts):
  // dieselbe AdminView wie oben, nur mit eigenen Reitern — kein Projekt noetig.
  'admin-vorlagen': 'plain',
  'admin-open-positions': 'plain',
  'admin-substitutions': 'plain',
  tasks: 'plain',
  'global-map': 'plain',
  settings: 'plain',
  'sent-reports': 'plain',
  crm: 'plain',
  'crm-map': 'plain',
  leads: 'plain',
  freelancers: 'plain',
  quotes: 'plain',
  invoices: 'plain',
  'erp-cockpit': 'plain',
  // Lernzentrum (docs/lernzentrum-tutorial-plan.md): steht fuer sich, braucht
  // kein geladenes Projekt — der Sprung aus einem Kapitel in die App geht
  // ueber die normale Projektauswahl.
  lernzentrum: 'plain',
  // SiFa-Vorlagen (docs/sifa-vorlagen-plan.md §3e): firmenweit, ueber allen
  // Kunden — gehoeren gerade zu keinem Projekt.
  'sifa-vorlagen': 'plain',
  // Materialverwaltung (docs/materialverwaltung-plan.md): Artikel und
  // Meldungen der Firma, an keinem Projekt.
  material: 'plain',
  // Talentpool (docs/talentpool-plan.md): Bewerbungen der Firma, an keinem
  // Projekt.
  talentpool: 'plain',

  'project-hub': 'project',
  'module-vorank': 'project',
  'module-unterlage': 'project',
  'module-sigeko-inspection': 'project',
  'module-sige-plan': 'project',
  'module-sige-einweisung': 'project',
  'module-dokumente': 'project',
  'module-planung': 'project',
  // SiFa-Dauerprojekte (docs/sifa-unterweisung-plan.md): beide Module lesen
  // ausschliesslich ihre eigenen Tabellen, brauchen aber das Projekt als Rahmen.
  'module-sifa-unterweisung': 'project',
  'module-sifa-mitarbeiter': 'project',
  'module-sifa-fristen': 'project',
  'module-sifa-taetigkeiten': 'project',
  // Gefaehrdungsbeurteilung (docs/sifa-gbu-plan.md): eigene Tabellen sifa_gbu*.
  'module-sifa-gbu': 'project',
  // Handbuch und Koordinierung haben keine eigene Kachel und keine eigene
  // Renderzeile mehr — beide liegen unter der SiGe-Plan-Huelle. Die WERTE
  // bleiben hier trotzdem gueltig, und das ist Absicht: in laufenden Tabs
  // liegen sie noch im sessionStorage. Wuerde man sie streichen, gaebe
  // `kindOf` undefined zurueck, `loadNavState` verwuerfe den Zustand und der
  // Nutzer landete nach F5 wortlos in der Uebersicht. So oeffnen sie die
  // Huelle am passenden Reiter (Zuordnung: SIGE_PLAN_REITER in App.tsx).
  'module-handbuch': 'project',
  'module-koordinierung': 'project',
  'module-asplan': 'project',
  'module-bauordnung': 'project',
  'module-archive': 'project',
  'module-sent-reports': 'project',
  editor: 'project',
};

/** Art einer Ansicht, oder undefined bei einem unbekannten Wert aus dem Storage. */
const kindOf = (view: string): 'project' | 'plain' | undefined =>
  (VIEW_KIND as Record<string, 'project' | 'plain' | undefined>)[view];

/**
 * Braucht diese Ansicht ein geoeffnetes Projekt?
 *
 * Einzige Quelle bleibt VIEW_KIND oben — der Record-Typ erzwingt Vollstaendigkeit.
 * Eine zweite Positivliste anderswo (App.tsx) wuerde beim naechsten neuen View
 * genau so still veralten wie die beiden Listen, die hier frueher standen.
 */
export const isProjectView = (view: AppView): boolean => VIEW_KIND[view] === 'project';

export const saveNavState = (storage: StorageLike, state: NavState): void => {
  if (!kindOf(state.view)) return;
  try {
    storage.setItem(NAV_STATE_KEY, JSON.stringify(state));
  } catch {
    // Quota/Private Mode — Restore ist nice-to-have, nie ein Fehler.
  }
};

export const loadNavState = (storage: StorageLike): NavState | null => {
  try {
    const raw = storage.getItem(NAV_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.view !== 'string') return null;
    const kind = kindOf(parsed.view);
    if (!kind) return null;
    if (kind === 'project' && typeof parsed.projectId !== 'string') return null;
    // Altformat (gespeichert bevor es die Schreibschutz-Flagge gab) und
    // Manipulation: ob das Projekt schreibgeschuetzt geoeffnet war, laesst sich
    // dann nicht mehr feststellen. Im Zweifel gar nicht wiederherstellen — der
    // Nutzer landet in der Uebersicht (harmlos), statt mit offenen Riegeln in
    // einem fremden Projekt (nicht harmlos). Betrifft nur Projekt-Ansichten,
    // projektlose Ansichten kommen weiter zurueck wie bisher.
    if (kind === 'project' && typeof parsed.isReadOnly !== 'boolean') return null;
    return {
      view: parsed.view as AppView,
      projectId: typeof parsed.projectId === 'string' ? parsed.projectId : null,
      activeSection: typeof parsed.activeSection === 'string' ? parsed.activeSection : null,
      isReadOnly: parsed.isReadOnly === true,
    };
  } catch {
    return null;
  }
};

export const clearNavState = (storage: StorageLike): void => {
  try { storage.removeItem(NAV_STATE_KEY); } catch { /* egal */ }
};
